import { DataTree } from '../types';

export interface Entity {
  id: string;
  name: string;
  slug: string;
  _archived: boolean;
  _draft: boolean;
}

interface MainCategory extends Entity {
  sortingNumber: number;
  subCategories: string[];
  genetics: boolean;
  infectology: boolean;
  clinicalLaboratory: boolean;
  allTests: boolean;
}

interface BuildMainTreeInputs {
  mainCategoriesData: any;
  subCategoriesData: any;
  testsData: any;
}

export const buildMainDataTree = ({
  mainCategoriesData,
  subCategoriesData,
  testsData
}: BuildMainTreeInputs): DataTree[] => {

  const mainCategories: MainCategory[] = mainCategoriesData
    .map((category: MainCategory) => ({
      id: category.id,
      name: category.name,
      slug: category.slug,
      _archived: category._archived,
      _draft: category._draft,
      sortingNumber: category.sortingNumber,
      subCategories: category.subCategories,
      genetics: category.genetics,
      infectology: category.infectology,
      clinicalLaboratory: category.clinicalLaboratory,
      allTests: category.allTests
    } as MainCategory))
    .filter((category: MainCategory) => !category._archived && !category._draft)
    .sort((a: { sortingNumber: number; }, b: { sortingNumber: number; }) => a.sortingNumber - b.sortingNumber);

  interface SubCategory extends Entity {
  }

  const subCategories: SubCategory[] = subCategoriesData
    .map((subCategory: SubCategory) => ({
      id: subCategory.id,
      name: subCategory.name,
      slug: subCategory.slug,
      _archived: subCategory._archived,
      _draft: subCategory._draft
    }))
    .filter((category: MainCategory) => !category._archived && !category._draft);

  interface Test extends Entity {
    subCategories: string[];
    categoryId: string;
    subCategoryId: string;
    airportCovidTest: boolean;
    genicaCovidTest: boolean;
    mobileLabCovidTest: boolean;
    sample: string;
    sortingNumber: string;
    duration: string;
    cardDescription: string;
    fullDescription: string;
    priceEur?: number;
    priceRange?: number;
  }

  const tests: Test[] = testsData
    .map((test: Test) => ({
      id: test.id,
      name: test.name,
      categoryId: test.categoryId,
      subCategoryId: test.subCategoryId,
      airportCovidTest: test.airportCovidTest,
      genicaCovidTest: test.genicaCovidTest,
      mobileLabCovidTest: test.mobileLabCovidTest,
      sample: test.sample,
      duration: test.duration,
      sortingNumber: test.sortingNumber,
      cardDescription: test.cardDescription,
      fullDescription: test.fullDescription,
      slug: test.slug,
      subCategories: test.subCategories,
      _archived: test._archived,
      _draft: test._draft,
      priceEur: test.priceEur,
      priceRange: test.priceRange
    }))
    .filter((category: MainCategory) => !category._archived && !category._draft)
    .sort((a: { sortingNumber: number; }, b: { sortingNumber: number; }) => a.sortingNumber - b.sortingNumber);

  interface SubCategoriesMap {
    [subCategoryID: string]: SubCategory;
  }

  const subCategoriesMap: SubCategoriesMap = subCategories
    .reduce((ac: SubCategoriesMap, subCategory: SubCategory) => ({
      ...ac,
      [subCategory.id]: subCategory
    }), {} as SubCategoriesMap);


  interface SubCategoryToTestsMap {
    [subCategoryID: string]: Test[];
  }

  const subCategoryToTestsMap: SubCategoryToTestsMap = tests
    .reduce((ac: SubCategoryToTestsMap, test: Test) => {
      const acc: SubCategoryToTestsMap = { ...ac };

      for (const subCategoryID of test.subCategories) {
        if (acc[subCategoryID] && Array.isArray(acc[subCategoryID])) {
          acc[subCategoryID] = [
            ...acc[subCategoryID],
            test
          ];
          continue;
        }

        acc[subCategoryID] = [test];
      }
      
      return acc;
    }, {} as SubCategoryToTestsMap);

  interface CategoryToTestsMap {
    [categoryID: string]: Test[];
  }

  const categoryToTestsMap: CategoryToTestsMap = tests
    .reduce((ac: CategoryToTestsMap, test: Test) => {
      const acc: CategoryToTestsMap = { ...ac };


        if (acc[test.categoryId] && Array.isArray(acc[test.categoryId])) {
          acc[test.categoryId] = [
            ...acc[test.categoryId],
            test
          ];
        }

        acc[test.categoryId] = [test];

      return acc;
    }, {} as SubCategoryToTestsMap);


  interface TreeSubCategory extends SubCategory {
    tests: Test[];
  }

  interface TreeMainCategory extends Omit<MainCategory, 'subCategories' | 'genetics' | 'infectology' | 'clinicalLaboratory' | 'allTests'> {
    subCategories: TreeSubCategory[];
  }

  type Tree = TreeMainCategory[];

  const tree: Tree = mainCategories
    .map((mainCategory: MainCategory) => ({
      ...mainCategory,
      subCategories: mainCategory.subCategories
        .map((subCategoryID: string) => ({
          ...subCategoriesMap[subCategoryID],
          tests: (subCategoryToTestsMap[subCategoryID] || [])
            .sort((a, b) => a.name.localeCompare(b.name))
            // @ts-ignore
            .sort((a, b) => a.sortingNumber - b.sortingNumber)
        } as TreeSubCategory)),
      tests: mainCategories
        .map((mainCategory: MainCategory) => (
          categoryToTestsMap[mainCategory.id]
        ))
    } as TreeMainCategory));
  
  return tree as any[];
};
